import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { validateAustralianMobileNumber } from '../../../../utils/validationUtils';
import { VALIDATION_ERROR_MESSAGE } from '../../../../constants/errorMessage';
import { useUpdateContactDetailsMutation } from '../../../../data/policy';
import { formatNumberWithoutSpaces, formatMobileNumberInput } from '../../../../utils/formatNumber';
import { EMAIL_REGEX } from '../../../../constants/constants';
import { FORM_KEYS } from '../../../../constants/formKeys';

const MOBILE_NUMBER_INCLUDE_SPACE_LENGTH = 12;

export const useUpdateContactDetails = ({
  setShowContactDetailsModal,
  setShowSuccessMessage,
  policyHolder,
  formContext
}) => {
  const { email, phoneMobile, policyNumber } = policyHolder;
  const { watch, setValue, trigger } = useFormContext();
  const [updateContactDetails, { isSuccess: isUpdateContactSuccess }] = useUpdateContactDetailsMutation();

  const newEmailValue = watch(FORM_KEYS.NEW_EMAIL);
  const newMobileNoValue = watch(FORM_KEYS.NEW_MOBILE_NUMBER);

  useEffect(() => {
    if (isUpdateContactSuccess) {
      setShowContactDetailsModal(false);
      setShowSuccessMessage(true);
    }
  }, [isUpdateContactSuccess, setShowContactDetailsModal, setShowSuccessMessage]);

  const onInputChange = useCallback(() => {
    const currentValue = watch(FORM_KEYS.NEW_MOBILE_NUMBER);
    const formattedValue = formatMobileNumberInput(currentValue);
    setValue(FORM_KEYS.NEW_MOBILE_NUMBER, formattedValue);
    trigger(FORM_KEYS.NEW_MOBILE_NUMBER);
  }, [setValue, trigger, watch]);

  const emailValidation = {
    required: 'Please enter your email.',
    pattern: {
      value: EMAIL_REGEX,
      message: 'Email address is invalid.'
    }
  };

  const mobileValidation = {
    minLength: {
      value: MOBILE_NUMBER_INCLUDE_SPACE_LENGTH,
      message: VALIDATION_ERROR_MESSAGE.INVALID_MOBILE_NUMBER
    },
    validate: {
      startsWith04: (value) => validateAustralianMobileNumber(value) || VALIDATION_ERROR_MESSAGE.INVALID_MOBILE_NUMBER
    }
  };

  const handleClose = () => {
    setShowContactDetailsModal(false);
  };

  const handleSubmit = async () => {
    const isValid = await formContext.trigger([FORM_KEYS.NEW_EMAIL, FORM_KEYS.NEW_MOBILE_NUMBER], {
      shouldFocus: true
    });

    if (isValid) {
      const updatedData = {
        policyNumber,
        phoneMobile: formatNumberWithoutSpaces(newMobileNoValue) || formatNumberWithoutSpaces(phoneMobile),
        email: newEmailValue || email,
        phoneWork: '',
        phoneHome: ''
      };
      updateContactDetails(updatedData);
    }
  };

  return {
    handleSubmit,
    handleClose,
    onInputChange,
    emailValidation,
    mobileValidation
  };
};
